/**
 * ActivePowerOutagesPage · v2.7.33 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Active Power Outages module — Oklahoma regional utility outages.
 * Polls /api/admin/weather/outages/{summary,cities,counties} every 10 s.
 *
 * Backend currently ships SYNTHETIC ingestion (no open feed for OG&E /
 * PSO / OEC) weighted by active NWS warning counties. Switch to real
 * scraping inside backend/routers/power_outages.py:_synthesize_snapshot.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Zap, RefreshCw, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import useCountUp from "@/hooks/useCountUp";

const fmt = (n) => Number(n || 0).toLocaleString();

export default function ActivePowerOutagesPage() {
  const [summary, setSummary] = useState(null);
  const [cities, setCities] = useState([]);
  const [counties, setCounties] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [a, b, c] = await Promise.all([
        adminApi.get("/admin/weather/outages/summary"),
        adminApi.get("/admin/weather/outages/cities"),
        adminApi.get("/admin/weather/outages/counties"),
      ]);
      setSummary(a.data || null);
      setCities(b.data?.rows || []);
      setCounties(c.data?.rows || []);
    } catch (e) {
      toast.error(`Outages load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { reload(); const id = setInterval(reload, 10000); return () => clearInterval(id); }, [reload]);

  const stateTotalAnim = useCountUp(summary?.state_total || 0, 700);

  const filteredCities = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((r) => r.city.toLowerCase().includes(q) || r.county.toLowerCase().includes(q));
  }, [cities, search]);
  const filteredCounties = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return counties;
    return counties.filter((r) => r.county.toLowerCase().includes(q));
  }, [counties, search]);

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="power-outages-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/weather" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider">
              <ArrowLeft className="h-4 w-4"/> Weather Commander
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <Zap className="h-6 w-6 text-amber-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">Active Power Outages · Oklahoma</h1>
          </div>
          <Button onClick={reload} disabled={busy} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold" data-testid="outages-refresh">
            <RefreshCw className="h-4 w-4 mr-2"/>Refresh
          </Button>
        </div>

        {/* Master state ticker */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-rose-500/10 border-amber-400/40 p-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #fbbf24 0%, transparent 50%)" }}/>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">Statewide affected meters · Oklahoma</div>
            {/* useCountUp already returns a comma-formatted string; do NOT
                re-wrap with Number()/.toLocaleString() — that yields NaN. */}
            <div className="text-6xl sm:text-7xl font-extrabold tabular-nums text-amber-200" data-testid="state-total-ticker">{stateTotalAnim}</div>
            <div className="text-xs text-zinc-400 mt-2 flex items-center gap-3 flex-wrap">
              <span><b className="text-zinc-200">{summary?.active_county_count || 0}</b> counties affected</span>
              <span className="text-zinc-700">·</span>
              <span><b className="text-rose-300">{summary?.storm_impacted_counties || 0}</b> under active storm warnings</span>
              <span className="text-zinc-700">·</span>
              <span>Updated {summary?.ts ? new Date(summary.ts).toLocaleTimeString() : "—"}</span>
            </div>
            {summary?.ingestion === "synthetic_demo_data" && (
              <div className="mt-3 text-[10px] flex items-center gap-1.5 text-amber-300/80" data-testid="synthetic-warning">
                <AlertTriangle className="h-3 w-3"/>
                <span className="font-bold">Synthetic ingestion</span> · OG&amp;E / PSO / OEC have no public outage feeds. Real ingestion requires per-utility scraping (Playwright). Data is weighted by active NWS warning counties.
              </div>
            )}
          </div>
        </Card>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500"/>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search city or county…" className="pl-8 bg-zinc-900 border-zinc-800 text-sm h-9" data-testid="outages-search"/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Cities */}
          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-sky-400/40 flex items-center justify-between">
              <span>City rollup</span>
              <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300">{filteredCities.length}</Badge>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto" data-testid="cities-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-900">
                  <tr className="text-left text-[9px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-3">City</th>
                    <th className="py-2 pr-3">County</th>
                    <th className="py-2 pr-3 text-right">Affected</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCities.length === 0 && <tr><td colSpan={3} className="py-4 text-center text-zinc-500">No outages.</td></tr>}
                  {filteredCities.map((r) => (
                    <tr key={r.city} className={`border-b border-zinc-800/60 ${r.under_storm ? "bg-rose-950/20" : ""}`}>
                      <td className="py-1.5 pr-3 font-bold text-zinc-100">
                        {r.city}
                        {r.under_storm && <span className="ml-1.5 text-[9px] uppercase text-rose-300 font-extrabold">⚡STORM</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-zinc-400">{r.county}</td>
                      <td className="py-1.5 pr-3 tabular-nums text-right text-amber-200 font-bold">{fmt(r.affected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Counties */}
          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-amber-400/40 flex items-center justify-between">
              <span>County rollup · all 77</span>
              <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300">{filteredCounties.length}</Badge>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto" data-testid="counties-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-900">
                  <tr className="text-left text-[9px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-3">County</th>
                    <th className="py-2 pr-3 text-right">Affected</th>
                    <th className="py-2 pr-3">Utilities</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCounties.map((r) => (
                    <tr key={r.county} className={`border-b border-zinc-800/60 ${r.under_storm ? "bg-rose-950/20" : ""}`}>
                      <td className="py-1.5 pr-3 font-bold text-zinc-100">
                        {r.county}
                        {r.under_storm && <span className="ml-1.5 text-[9px] uppercase text-rose-300 font-extrabold">⚡</span>}
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums text-right font-bold">
                        <span className={r.affected > 0 ? "text-amber-200" : "text-zinc-600"}>{fmt(r.affected)}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-zinc-500 text-[10px]">
                        {Object.entries(r.by_utility || {}).slice(0, 3).map(([util, n]) => `${util}: ${fmt(n)}`).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
